import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useScreenshot } from '../../contexts/Screenshot';
import Screenshot from './Screenshot';
import ScreenshotDialog from './ScreenshotDialog';
import styles from './ScreenshotThumbnail.css';
import type { BoundingBox } from '../../api-client';

interface Props {
  screenshotId: string;
  issueHelp: string;
  issueOrder: number;
  boundingBox?: BoundingBox | null;
}

const ScreenshotThumbnail: React.FC<Props> = ({
  screenshotId,
  boundingBox = null,
  issueHelp,
  issueOrder
}) => {
  const { t } = useTranslation();
  const [showScreenshotDialog, setShowScreenshotDialog] = useState(false);
  const { screenshot, loading, error } = useScreenshot(screenshotId);
  const blob = screenshot?.blob || null;
  const metadata = screenshot?.metadata || null;
  const disableFullImageButton = loading || !!error || !blob;

  const thumbnailStyles = useMemo(() => {
    if (!metadata || !boundingBox) {
      return;
    }

    const widthPercentage = Math.floor(
      ((boundingBox.x + boundingBox.width / 2) / metadata.width) * 100
    );
    const heightPercentage = Math.floor(
      ((boundingBox.y + boundingBox.height / 2) / metadata.height) * 100
    );
    return {
      objectPosition: `${widthPercentage}% ${heightPercentage}%`
    } as React.CSSProperties;
  }, [metadata, boundingBox]);

  return (
    <div>
      <div
        data-testid="screenshot-action"
        className={classNames(styles.thumbnail, {
          [styles.wholepage]: !!boundingBox
        })}
        onClick={() => setShowScreenshotDialog(true)}
      >
        <Screenshot
          screenshot={blob}
          loading={loading}
          error={error}
          screenshotStyles={thumbnailStyles}
        />
      </div>
      <button
        className="Link"
        onClick={() => setShowScreenshotDialog(true)}
        disabled={disableFullImageButton}
      >
        {t('click to see full image')}
      </button>
      {blob && (
        <ScreenshotDialog
          showDialog={showScreenshotDialog}
          onClose={() => setShowScreenshotDialog(false)}
          screenshot={blob}
          metadata={metadata}
          boundingBox={boundingBox}
          issueHelp={issueHelp}
          issueOrder={issueOrder}
        />
      )}
    </div>
  );
};

export default ScreenshotThumbnail;
